/**
 * MotionModeContext
 *
 * SHS Visual Experience System — Sprint 29.1A.
 *
 * Purpose:
 *   Provide the canonical motion posture for the SHS platform. The mode
 *   informs future visual primitives whether they may animate, and if so,
 *   how expressively.
 *
 * Canonical modes:
 *   - "discovery"  cinematic surfaces (marketing/education discovery)
 *   - "clinical"   calm, precise surfaces (assessments, dashboards, admin)
 *   - "reduced"    accessibility override — OS/browser has requested reduced motion
 *
 * Governing rule (per Founder directive, Sprint 29.1A):
 *   REDUCED MOTION OVERRIDES ALL OTHER MOTION MODES.
 *   If prefers-reduced-motion: reduce is set, mode is "reduced" regardless
 *   of route classification.
 *
 * Safe fallback:
 *   Any route not explicitly classified as Discovery resolves to Clinical.
 *   Clinical is the safest default. Unknown/retired/admin/payment/auth all
 *   remain Clinical.
 *
 * Placement:
 *   The provider must live INSIDE <BrowserRouter/> because it consumes
 *   useLocation(). It sits above all business-logic providers so that any
 *   future consumer can read the mode without extra plumbing.
 *
 * Side effects (intentional, minimal):
 *   Writes `data-experience-mode="{mode}"` to <html> (document.documentElement).
 *   Chosen over <body> because <html lang> is already the canonical scoping
 *   root for language; this keeps every scoping attribute at the same level.
 *   The attribute is the CSS scoping boundary for the future Living Sleep
 *   System but has NO visual effect in 29.1A because no CSS is scoped
 *   against it yet (only reduced-motion accessibility rules).
 *
 * Non-goals:
 *   - Does not change routing.
 *   - Does not change redirects.
 *   - Does not modify any component.
 *   - Does not animate anything.
 */

import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { usePrefersReducedMotion } from '../experience/hooks/usePrefersReducedMotion';

const MotionModeContext = createContext(null);

// ---------------------------------------------------------------------------
// Route classification
// ---------------------------------------------------------------------------
//
// Conservative initial classification (Sprint 29.1A). Discovery is opt-in.
// Anything not matched here — including /admin/*, /payment/*, /sleep-*,
// retired routes, and any unclassified path — falls back to Clinical.
//
// Path matching is exact for the root ('/') and prefix-based for all others.
// Prefix rules use a trailing slash to avoid accidental matches
// (e.g. '/academy' must not swallow '/academy-something-else').

const DISCOVERY_EXACT = new Set([
  '/',
  '/topics',
  '/about',
  '/authority',
  '/academy',
]);

const DISCOVERY_PREFIXES = [
  '/topic/',
  '/academy/sleep-reset', // /academy/sleep-reset and any subpath
];

/**
 * classifyRoute(pathname)
 *
 * Returns 'discovery' or 'clinical'. Never returns 'reduced' — that is
 * strictly an accessibility override applied after route classification.
 *
 * Exported for tests and for future admin diagnostics.
 */
export function classifyRoute(pathname) {
  if (typeof pathname !== 'string' || pathname.length === 0) {
    return 'clinical';
  }

  if (DISCOVERY_EXACT.has(pathname)) {
    return 'discovery';
  }

  for (const prefix of DISCOVERY_PREFIXES) {
    // Exact prefix match OR prefix followed by '/' OR the pathname equals
    // the prefix without trailing slash (e.g. '/academy/sleep-reset').
    if (
      pathname === prefix ||
      pathname === prefix.replace(/\/$/, '') ||
      pathname.startsWith(prefix)
    ) {
      return 'discovery';
    }
  }

  return 'clinical';
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const MotionModeProvider = ({ children }) => {
  const location = useLocation();
  const prefersReducedMotion = usePrefersReducedMotion();

  const routeMode = useMemo(
    () => classifyRoute(location.pathname),
    [location.pathname]
  );

  const mode = prefersReducedMotion ? 'reduced' : routeMode;

  // Expose the effective mode as a stable DOM attribute on <html>.
  // Kept at the document root so future CSS scoping rules (e.g.
  // `[data-experience-mode="discovery"] .some-primitive { ... }`) work
  // without wrapping the entire app in another <div>.
  useEffect(() => {
    if (typeof document === 'undefined' || !document.documentElement) return;
    document.documentElement.setAttribute('data-experience-mode', mode);
  }, [mode]);

  const value = useMemo(
    () => ({
      mode,
      routeMode,
      prefersReducedMotion,
      isReduced: mode === 'reduced',
      isDiscovery: mode === 'discovery',
      isClinical: mode === 'clinical',
    }),
    [mode, routeMode, prefersReducedMotion]
  );

  return (
    <MotionModeContext.Provider value={value}>
      {children}
    </MotionModeContext.Provider>
  );
};

/**
 * useMotionMode
 *
 * Consume the current motion posture. Safe to call outside the provider
 * (e.g. in unit tests) — returns a Clinical, non-reduced default rather
 * than throwing, so the calling component still renders correctly.
 */
export const useMotionMode = () => {
  const ctx = useContext(MotionModeContext);
  if (ctx) return ctx;
  return {
    mode: 'clinical',
    routeMode: 'clinical',
    prefersReducedMotion: false,
    isReduced: false,
    isDiscovery: false,
    isClinical: true,
  };
};

export default MotionModeContext;
